/**
 * @file schemas/clients.ts
 * @description 客戶（組織）管理 Zod 校驗 schema（供前端與後端共用）。
 */
import { z } from 'zod';
import { VERIFICATION_SCHEMES } from '../enums';
const optionalNullableString = (max) => {
    const schema = max === undefined ? z.string() : z.string().max(max);
    return schema.optional().nullable();
};
// 通路（CHANNEL_TYPES / clientChannelInputSchema）與驗證選項（VERIFICATION_OPTIONS）已移除：
// 屬於每次派案的申請明細，改由 cases schema 承載（@zhcb/shared schemas/cases.ts：
// SALES_CHANNELS / CASE_VERIFICATION_OPTIONS）。2026-06-27 客戶/派案欄位解耦。
/** 主聯絡人輸入（圖「聯絡人」相關欄位） */
export const clientContactInputSchema = z.object({
    name: z.string().min(1).max(100),
    title: optionalNullableString(100),
    phone: optionalNullableString(30),
    mobile: optionalNullableString(30),
    fax: optionalNullableString(30),
    email: z.string().email().optional().nullable(),
    isPrimary: z.boolean().default(true),
});
/** 驗證場所輸入（圖「驗證場所地址」，可多筆，僅地址） */
export const clientLandInputSchema = z.object({
    address: z.string().min(1),
});
/** 採收後處理場所輸入（圖「采收後處理場所地址」，可多筆，僅地址） */
export const clientProcessingSiteInputSchema = z.object({
    address: z.string().min(1),
});
/**
 * 編輯客戶時的場所地址輸入（2026-09-25 序33：編輯客戶可新增／修改驗證場所與採收後處理場所）。
 * - 帶 `id` → 更新既有場所地址；
 * - 不帶 `id` → 新增場所；
 * - 地址留空 → 視為刪除（帶 id 時軟刪除，不帶 id 時忽略）。
 */
export const clientSiteUpsertInputSchema = z.object({
    id: z.number().int().positive().optional(),
    address: z.string().max(500).optional().nullable(),
});
/**
 * 驗證註冊碼格式（2026-09-15 回報序25：允許手動指定／修改）。
 * 前綴與驗證方案是否相符由 API 層以 isClientCodeOfScheme 再校驗。
 */
export const clientCodeInputSchema = z
    .string()
    .trim()
    .toUpperCase()
    .regex(/^ZH-[A-Z]{2,4}-?\d{4,}$/, '驗證註冊碼格式須如 ZH-TAP1285');
/**
 * 建立客戶請求 schema。
 * clientCode 預設由後端 nextNumber() 自動接續編號；已有註冊碼的客戶可手動填入（序25）。
 */
export const createClientSchema = z.object({
    name: z.string().min(1).max(200),
    /**
     * 驗證方案（TOP / TAP / ECO）。客戶綁定單一方案，決定驗證註冊碼前綴
     * （TOP→CAS、TAP→TAP、ECO→ECO），如 `ZH-CAS-0001`（2026-07-01 恢復）。
     * 建立後不可變更（clientCode 由此衍生），故僅出現在建立 schema。
     */
    scheme: z.enum(VERIFICATION_SCHEMES),
    /** 驗證註冊碼（選填；留空由系統自動接續編號，已有註冊碼者可手動填入） */
    clientCode: clientCodeInputSchema.optional(),
    // 其餘驗證業務屬性（類型 / 類別 / 合約來源 / 驗證資格 / 驗證選項 / 農戶數 /
    // 輔導員 / 通路 / 驗證組備註 / 管理組備註）仍於派案（建立案件）時採集。
    taxId: optionalNullableString(30),
    /** 組織代碼（TAP 專用；身份類編碼，留在客戶） */
    orgCode: optionalNullableString(50),
    // —— 法人（法定代表人，含身份證號） ——
    /** 法人姓名 */
    representativeName: optionalNullableString(100),
    /** 法人身份證號（僅法人需要） */
    representativeIdNo: optionalNullableString(50),
    /** 法人電話 */
    representativeMobile: optionalNullableString(30),
    /** 法人 Email */
    representativeEmail: z.string().email().optional().nullable(),
    // —— 負責人（無身份證號，常與法人為同一人） ——
    /** 負責人姓名 */
    personInChargeName: optionalNullableString(100),
    /** 負責人電話 */
    personInChargePhone: optionalNullableString(30),
    /** 負責人 Email */
    personInChargeEmail: z.string().email().optional().nullable(),
    /** 戶籍地址 */
    registeredAddress: optionalNullableString(),
    /** 通訊地址 */
    contactAddress: optionalNullableString(),
    /** 寄件地址 */
    mailingAddress: optionalNullableString(),
    /** 郵遞區號 */
    postalCode: optionalNullableString(10),
    /** 客戶備註 */
    clientNotes: optionalNullableString(),
    /** 驗證資格（主體類型多選，序102 自新建案件遷移至客戶檔案；值取自驗證資格字典 name） */
    verificationQualifications: z.array(z.string().max(50)).optional().nullable(),
    // 公司級 phone/email 已移除：改由法人/負責人/聯絡人三角色各自帶聯繫方式。
    // —— 嵌套子表（僅用於新建一次性建檔） ——
    /** 聯絡人（新建表單收集主聯絡人，可多筆） */
    contacts: z.array(clientContactInputSchema).optional(),
    /** 驗證場所地址（可多筆） */
    lands: z.array(clientLandInputSchema).optional(),
    /** 採收後處理場所地址（可多筆） */
    processingSites: z.array(clientProcessingSiteInputSchema).optional(),
});
/**
 * 更新客戶請求 schema（所有欄位可選）。
 * 排除批量聯絡人子表（contacts），但放行單條 `primaryContact`：編輯客戶時就地 upsert 主聯絡人（isPrimary=true）。
 * 序33（2026-09-25）：放行 `lands` / `processingSites` 全量清單——帶上時以「清單即最終狀態」同步
 * （帶 id 更新、無 id 新增、清單中缺席或地址留空的既有場所軟刪除）；省略時不動場所子表。
 */
export const updateClientSchema = createClientSchema
    // scheme 建立後不可變更（clientCode 由其衍生），排除於更新 schema。
    .omit({ scheme: true, contacts: true, lands: true, processingSites: true })
    .partial()
    .extend({
    /** 主聯絡人（編輯模式 upsert；name 為空字串/未填視為不變更或清除既有主聯絡人） */
    primaryContact: clientContactInputSchema.optional(),
    /** 驗證場所地址全量清單（序33；省略 = 不變更） */
    lands: z.array(clientSiteUpsertInputSchema).optional(),
    /** 採收後處理場所地址全量清單（序33；省略 = 不變更） */
    processingSites: z.array(clientSiteUpsertInputSchema).optional(),
});
/** 管理組可執行的客戶驗證資格生命週期動作。 */
export const CLIENT_VERIFICATION_LIFECYCLE_ACTIONS = [
    'pause',
    'resume',
    'revoke',
    'terminate',
    'end',
];
/**
 * 客戶驗證資格生命週期異動輸入。
 *
 * 每次人工異動都要求原因，以支撐調查處置及後續稽核追溯；目標狀態由後端依
 * 客戶方案與 action 推導，前端不得直接傳任意狀態繞過狀態機。
 */
export const changeClientVerificationStatusSchema = z.object({
    action: z.enum(CLIENT_VERIFICATION_LIFECYCLE_ACTIONS),
    reason: z.string().trim().min(2, '請填寫至少 2 字的異動原因').max(1000),
});
